import gql from 'graphql-tag'

const BILLING_ACCOUNTS_QUERY = gql`
  query {
    billing {
      accounts: getAccounts {
        id: _id
        account {
          id
          name
          system {
            id
          }
        }
      }
    }
  }
`

export const REPORT_QUERY_KEYS = { system: 's', account: 'a', name: 'n' }

export function platformAccountId (account = {}) {
  const id = account.legacyAccountId ?? account.value ?? account.id
  if (id == null || Number(id) === 0) return undefined
  return id
}

export function isAllAccountsSelected (account = {}) {
  return platformAccountId(account) == null
}

export function syncWorkingAccount (store, account) {
  const id = platformAccountId(account)
  if (id == null) return

  store.commit('setWorkingAccount', {
    value: id,
    label: account.label || account.name || `#${id}`,
    legacyAccountId: id,
    billingAccountId: account.billingAccountId,
    system: account.system
  })
}

export function parseReportRouteQuery (route) {
  const q = route.query
  const k = REPORT_QUERY_KEYS
  return {
    systemId: q[k.system] ?? q.systemId,
    accountId: q[k.account] ?? q.accountId,
    accountName: q[k.name] ?? q.accountName ?? ''
  }
}

export function workingAccountToReportSelection (account = {}) {
  const id = platformAccountId(account)
  if (id == null) return null

  const systemId = account.system?.id || 'default'
  return {
    id,
    value: id,
    label: account.label || account.name || `#${id}`,
    name: account.label || account.name || `#${id}`,
    system: {
      id: systemId,
      name: account.system?.name || systemId
    }
  }
}

export function buildReportRouteQuery (account = {}) {
  const selection = workingAccountToReportSelection(account)
  if (!selection) return null

  const k = REPORT_QUERY_KEYS
  const query = {
    [k.system]: String(selection.system.id),
    [k.account]: String(selection.id)
  }
  const name = selection.label || ''
  if (name) query[k.name] = name
  return query
}

let billingAccountsCache = null
let billingAccountsFetch = null

export function seedBillingAccountsCache (accounts) {
  if (Array.isArray(accounts) && accounts.length) {
    billingAccountsCache = accounts
  }
}

function findBillingAccountIdInList (accounts, platformId) {
  const wanted = String(platformId)
  const match = accounts.find(
    item => item?.account?.id != null && String(item.account.id) === wanted
  )
  return match?.id
}

async function fetchBillingAccounts (apollo) {
  if (billingAccountsCache) {
    return billingAccountsCache
  }

  if (!billingAccountsFetch) {
    billingAccountsFetch = apollo.query({
      query: BILLING_ACCOUNTS_QUERY,
      fetchPolicy: 'no-cache'
    }).then(({ data }) => {
      billingAccountsCache = data?.billing?.accounts || []
      return billingAccountsCache
    }).finally(() => {
      billingAccountsFetch = null
    })
  }

  return billingAccountsFetch
}

export function warmBillingAccountsCache (apollo) {
  return fetchBillingAccounts(apollo).catch(() => [])
}

export async function findBillingAccountId (apollo, platformId) {
  const accounts = await fetchBillingAccounts(apollo)
  return findBillingAccountIdInList(accounts, platformId)
}

export async function resolveBillingReportSystemId (apollo, account) {
  if (account?.system?.id) return account.system.id

  const platformId = platformAccountId(account)
  if (platformId == null) return 'default'

  try {
    const accounts = await fetchBillingAccounts(apollo)
    const wanted = String(platformId)
    const matched = accounts.find(
      item => item?.account?.id != null && String(item.account.id) === wanted
    )
    return matched?.account?.system?.id || 'default'
  } catch (err) {
    console.error('billing-navigation:', err.message)
    return 'default'
  }
}

function push (router, location) {
  return router.push(location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}

export async function goToBillingAccount (router, store, apollo) {
  const account = store.state.workingAccount || {}

  if (account.billingAccountId) {
    return push(router, `/billing/accounts/update/${account.billingAccountId}`)
  }

  const platformId = platformAccountId(account)
  if (platformId == null) {
    return push(router, '/billing/accounts')
  }

  const cachedId = billingAccountsCache
    ? findBillingAccountIdInList(billingAccountsCache, platformId)
    : null
  if (cachedId) {
    return push(router, `/billing/accounts/update/${cachedId}`)
  }

  try {
    const billingId = await findBillingAccountId(apollo, platformId)
    return push(router, billingId
      ? `/billing/accounts/update/${billingId}`
      : '/billing/accounts')
  } catch (err) {
    console.error('Billing Accounts navigation:', err.message)
    return push(router, '/billing/accounts')
  }
}

export async function goToBillingReport (router, store, apollo) {
  const account = store.state.workingAccount || {}
  const platformId = platformAccountId(account)

  if (platformId == null) {
    return push(router, '/billing/report')
  }

  const systemId = await resolveBillingReportSystemId(apollo, account)
  const query = buildReportRouteQuery({
    ...account,
    system: { id: systemId, name: account.system?.name || systemId }
  })

  return push(router, {
    path: '/billing/report',
    query: query || undefined
  })
}

export async function goToPausedDevices (router, store, apollo) {
  const account = store.state.workingAccount || {}
  const platformId = platformAccountId(account)

  if (platformId == null) return

  const goToEditor = billingId => push(router, {
    path: `/billing/accounts/update/${billingId}`,
    query: { managePausedDevices: 'true' }
  })

  if (account.billingAccountId) {
    return goToEditor(account.billingAccountId)
  }

  try {
    const billingId = await findBillingAccountId(apollo, platformId)
    return billingId ? goToEditor(billingId) : push(router, '/billing/accounts')
  } catch (err) {
    console.error('Manage Paused Devices navigation:', err.message)
    return push(router, '/billing/accounts')
  }
}
